import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { setCustomCategoryMap } from '@/lib/categories';
import { useTranslation } from 'react-i18next';

export interface CustomCategory {
  id: string;
  label: string;
  emoji: string;
  isCustom: true;
  createdAt: string;
}

// Default built-in categories
export const BUILTIN_CATEGORIES = [
  { id: 'food', label: 'Food & Dining', emoji: '🍕', isCustom: false },
  { id: 'groceries', label: 'Groceries', emoji: '🛒', isCustom: false },
  { id: 'transport', label: 'Transportation', emoji: '🚗', isCustom: false },
  { id: 'shopping', label: 'Shopping', emoji: '🛍️', isCustom: false },
  { id: 'entertainment', label: 'Entertainment', emoji: '🎬', isCustom: false },
  { id: 'subscriptions', label: 'Subscriptions', emoji: '📺', isCustom: false },
  { id: 'utilities', label: 'Bills & Utilities', emoji: '⚡', isCustom: false },
  { id: 'health', label: 'Healthcare', emoji: '🏥', isCustom: false },
  { id: 'education', label: 'Education', emoji: '📚', isCustom: false },
  { id: 'travel', label: 'Travel', emoji: '✈️', isCustom: false },
  { id: 'home', label: 'Home & Garden', emoji: '🏠', isCustom: false },
  { id: 'loans', label: 'Loans', emoji: '🏦', isCustom: false },
  { id: 'credit_cards', label: 'Credit Cards', emoji: '💳', isCustom: false },
  { id: 'other', label: 'Other', emoji: '📦', isCustom: false },
] as const;

export type BuiltinCategory = {
  id: typeof BUILTIN_CATEGORIES[number]['id'];
  label: string;
  emoji: string;
  isCustom: false;
};
export type Category = BuiltinCategory | CustomCategory;

export function useCustomCategories() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const [customCategories, setCustomCategories] = useState<CustomCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load custom categories from Supabase
  useEffect(() => {
    const fetchCategories = async () => {
      if (!user) {
        setCustomCategories([]);
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('custom_categories')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: true });

        if (error) throw error;

        const mapped: CustomCategory[] = (data || []).map(row => ({
          id: row.id,
          label: row.label,
          emoji: row.emoji,
          isCustom: true,
          createdAt: row.created_at,
        }));

        setCustomCategories(mapped);
        // Populate global lookup so getCategoryLabel/Emoji can resolve UUIDs
        setCustomCategoryMap(mapped.map(c => ({ id: c.id, label: c.label, emoji: c.emoji })));
      } catch (error) {
        console.error('Failed to load custom categories:', error);
        setCustomCategories([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [user]);

  // Add a new custom category
  const addCustomCategory = useCallback(async (label: string, emoji: string = '🏷️'): Promise<CustomCategory | null> => {
    if (!user) return null;
    
    const trimmedLabel = label.trim();
    if (!trimmedLabel) return null;

    // Check if category already exists (builtin or custom)
    const existsInBuiltin = BUILTIN_CATEGORIES.some(
      c => c.label.toLowerCase() === trimmedLabel.toLowerCase()
    );
    const existsInCustom = customCategories.some(
      c => c.label.toLowerCase() === trimmedLabel.toLowerCase()
    );

    if (existsInBuiltin || existsInCustom) {
      return null; // Category already exists
    }

    try {
      const { data, error } = await supabase
        .from('custom_categories')
        .insert({
          user_id: user.id,
          label: trimmedLabel,
          emoji,
        })
        .select()
        .single();

      if (error) throw error;

      const newCategory: CustomCategory = {
        id: data.id,
        label: data.label,
        emoji: data.emoji,
        isCustom: true,
        createdAt: data.created_at,
      };

      setCustomCategories(prev => {
        const next = [...prev, newCategory];
        setCustomCategoryMap(next.map(c => ({ id: c.id, label: c.label, emoji: c.emoji })));
        return next;
      });
      return newCategory;
    } catch (error) {
      console.error('Failed to add custom category:', error);
      return null;
    }
  }, [user, customCategories]);

  // Update an existing custom category
  const updateCustomCategory = useCallback(async (categoryId: string, label: string, emoji: string): Promise<boolean> => {
    if (!user) return false;
    
    const trimmedLabel = label.trim();
    if (!trimmedLabel) return false;

    // Check if new label conflicts with other categories
    const existsInBuiltin = BUILTIN_CATEGORIES.some(
      c => c.label.toLowerCase() === trimmedLabel.toLowerCase()
    );
    const existsInCustom = customCategories.some(
      c => c.id !== categoryId && c.label.toLowerCase() === trimmedLabel.toLowerCase()
    );

    if (existsInBuiltin || existsInCustom) {
      return false; // Category name already exists
    }

    try {
      const { error } = await supabase
        .from('custom_categories')
        .update({ label: trimmedLabel, emoji })
        .eq('id', categoryId)
        .eq('user_id', user.id);

      if (error) throw error;

      setCustomCategories(prev => {
        const next = prev.map(c =>
          c.id === categoryId ? { ...c, label: trimmedLabel, emoji } : c
        );
        setCustomCategoryMap(next.map(c => ({ id: c.id, label: c.label, emoji: c.emoji })));
        return next;
      });
      return true;
    } catch (error) {
      console.error('Failed to update custom category:', error);
      return false;
    }
  }, [user, customCategories]);

  // Remove a custom category
  const removeCustomCategory = useCallback(async (categoryId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('custom_categories')
        .delete()
        .eq('id', categoryId)
        .eq('user_id', user.id);

      if (error) throw error;

      setCustomCategories(prev => prev.filter(c => c.id !== categoryId));
      return true;
    } catch (error) {
      console.error('Failed to remove custom category:', error);
      return false;
    }
  }, [user]);

  // Get all categories (builtin + custom)
  const allCategories = useMemo(() => {
    const categories: Category[] = [
      ...BUILTIN_CATEGORIES.map((category) => ({
        ...category,
        label: t(`categories.${category.id}`),
      })),
      ...customCategories,
    ];
    return categories;
  }, [customCategories, t]);

  // Find category by ID or label
  const findCategory = useCallback((idOrLabel: string): Category | undefined => {
    const normalized = idOrLabel.toLowerCase();
    const builtin = BUILTIN_CATEGORIES.find(
      c => c.id === idOrLabel || c.label.toLowerCase() === normalized
    );
    if (builtin) {
      return allCategories.find(c => c.id === builtin.id);
    }
    return allCategories.find(c => c.id === idOrLabel || c.label.toLowerCase() === normalized);
  }, [allCategories]);

  // Get category display info (emoji + label) by ID
  const getCategoryDisplay = useCallback((categoryId: string): { emoji: string; label: string } => {
    const category = findCategory(categoryId);
    if (category) {
      return { emoji: category.emoji, label: category.label };
    }
    // Fallback for unknown categories
    return { emoji: '📝', label: categoryId };
  }, [findCategory]);

  return {
    customCategories,
    allCategories,
    addCustomCategory,
    updateCustomCategory,
    removeCustomCategory,
    findCategory,
    getCategoryDisplay,
    builtinCategories: BUILTIN_CATEGORIES,
    isLoading,
  };
}